/**
 * lib/push.ts — NOT-148 web-push client helpers.
 *
 * Pure browser-side helpers for:
 *   1. registering the service worker
 *   2. querying push support + current permission
 *   3. subscribing via PushManager + the server's VAPID public key
 *   4. POSTing the subscription to the API
 *   5. unsubscribing (DELETE)
 */

import { apiRequest } from './api-client';

export interface PushSupport {
  /** True when the browser exposes ServiceWorker + PushManager + Notification. */
  supported: boolean;
  /** Current Notification.permission value. */
  permission: NotificationPermission | null;
}

export function detectPushSupport(): PushSupport {
  if (typeof window === 'undefined') return { supported: false, permission: null };
  const supported =
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
  return {
    supported,
    permission: supported ? Notification.permission : null,
  };
}

/** Convert the base64-url-encoded VAPID public key to the Uint8Array form
 *  PushManager.subscribe expects. */
function vapidKeyToUint8(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

interface VapidKeyResponse {
  public_key: string;
}

interface SubscribeResponse {
  id: string;
  endpoint: string;
}

/** Full subscription flow: request permission → register SW → fetch VAPID
 *  public key → call PushManager.subscribe → POST to API. Returns the
 *  registered endpoint on success. */
export async function subscribeForPush(accessToken: string): Promise<string> {
  if (!detectPushSupport().supported) {
    throw new Error('push_unsupported');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('push_permission_denied');
  }
  const registration = await ensureServiceWorker();
  const vapid = await apiRequest<VapidKeyResponse>('/api/v1/push/vapid-public-key', {
    method: 'GET',
    accessToken,
  });
  if (!vapid.public_key) {
    throw new Error('push_not_configured');
  }
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidKeyToUint8(vapid.public_key),
  });
  const sub = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!sub.endpoint || !sub.keys?.p256dh || !sub.keys?.auth) {
    throw new Error('push_subscription_malformed');
  }
  await apiRequest<SubscribeResponse>('/api/v1/push/subscriptions', {
    method: 'POST',
    accessToken,
    body: {
      endpoint: sub.endpoint,
      keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
      user_agent: navigator.userAgent.slice(0, 500),
    },
  });
  return sub.endpoint;
}

export async function unsubscribeFromPush(accessToken: string): Promise<void> {
  if (!detectPushSupport().supported) return;
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await subscription.unsubscribe();
  await apiRequest('/api/v1/push/subscriptions', {
    method: 'DELETE',
    accessToken,
    body: { endpoint: subscription.endpoint },
  });
}

export async function currentSubscriptionEndpoint(): Promise<string | null> {
  if (!detectPushSupport().supported) return null;
  const registration = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!registration) return null;
  const sub = await registration.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}
